type GuidedParseResult = {
  filter: Record<string, any> | null;
  error: string;
};

export type GuidedComponentHelp = {
  description: string;
  required: string[];
  recommended: string[];
  notes: string[];
};

const DEFAULT_COMPONENT_HELP: GuidedComponentHelp = {
  description: "General-purpose option filter.",
  required: ["facetTitle", "component", "applyToColumn"],
  recommended: [
    "facetIdentifier",
    "sourceTable",
    "filterLabelAttribute",
    "filterValueAttribute",
    "sortColumn",
    "sortDirection",
  ],
  notes: [
    "Use Test source to validate sourceSchema/sourceTable/attributes before saving.",
  ],
};

const COMPONENT_HELP: Record<string, GuidedComponentHelp> = {
  CheckboxFilter: {
    ...DEFAULT_COMPONENT_HELP,
    description: "Shows a list of selectable options.",
  },
  OntologyFilter: {
    description: "Builds hierarchical options from ontology terms.",
    required: ["facetTitle", "component", "applyToColumn", "sourceTable"],
    recommended: [
      "facetIdentifier",
      "filterLabelAttribute",
      "filterValueAttribute",
      "ontologyIdentifiers",
      "sortColumn",
      "sortDirection",
    ],
    notes: [
      "Set ontologyIdentifiers when multiple ontologies share one source table.",
      "Ontology filters typically use label/name-like attributes.",
    ],
  },
  ToggleFilter: {
    description: "Single on/off option mapped to a boolean-like column.",
    required: [
      "facetTitle",
      "component",
      "applyToColumn",
      "trueOption.text",
    ],
    recommended: ["facetIdentifier", "trueOption.value"],
    notes: [
      "Validator requires trueOption and trueOption.text for ToggleFilter.",
    ],
  },
  ServiceFilter: {
    ...DEFAULT_COMPONENT_HELP,
    description: "Service-focused option filter with source-backed values.",
  },
  StringFilter: {
    description: "Free-text filter. It does not require applyToColumn validation.",
    required: ["facetTitle", "component"],
    recommended: ["facetIdentifier", "negotiatorRequestString"],
    notes: [
      "StringFilter is the only component where applyToColumn is optional.",
    ],
  },
};

function cloneFilter(filter: Record<string, any>) {
  return JSON.parse(JSON.stringify(filter));
}

export function parseEditorFilter(filterJson: string): GuidedParseResult {
  try {
    const parsed = JSON.parse(filterJson);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {
        filter: null,
        error: "Filter JSON must be an object.",
      };
    }

    return {
      filter: parsed,
      error: "",
    };
  } catch {
    return {
      filter: null,
      error: "Guided editor is disabled while JSON is invalid.",
    };
  }
}

export function setGuidedFilterValue(
  filter: Record<string, any>,
  path: string,
  value: any
): Record<string, any> {
  const updated = cloneFilter(filter);
  const keys = path.split(".");
  let cursor: Record<string, any> = updated;

  for (let index = 0; index < keys.length - 1; index++) {
    const key = keys[index];
    if (!cursor[key] || typeof cursor[key] !== "object") {
      cursor[key] = {};
    }

    cursor = cursor[key];
  }

  cursor[keys[keys.length - 1]] = value;
  return updated;
}

export function parseCommaSeparatedValues(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function formatFilterJson(filter: Record<string, any>): string {
  return JSON.stringify(filter, null, 2);
}

export function getComponentHelp(component: string): GuidedComponentHelp {
  return COMPONENT_HELP[component] || DEFAULT_COMPONENT_HELP;
}
